import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { InvoicePreview, type PreviewLine } from '@/client/components/invoices/InvoicePreview';
import {
  type InvoiceStatus,
  InvoiceStatusBadge,
} from '@/client/components/invoices/InvoiceStatusBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Checkbox } from '@/client/components/ui/checkbox';
import { DatePicker } from '@/client/components/ui/date-picker';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Textarea } from '@/client/components/ui/textarea';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { type Currency, currencySymbol, fmtAmount } from '@/client/lib/currency';
import { toISODate } from '@/client/lib/dates';
import { type RouterOutputs, trpc } from '@/client/utils/trpc';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check, Download, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/invoices/$id')({
  component: InvoiceDetailPage,
});

type InvoiceDetail = RouterOutputs['invoices']['get'];

type LineItem = {
  id: string;
  timesheet_entry_id: string | null;
  person_id: string;
  person_name: string;
  date: string;
  description: string;
  hours: number;
  sort_order: number;
  included: boolean;
  is_shadow: boolean;
  is_manual: boolean;
};

function getLast13Months(currentValue?: string): { value: string; label: string }[] {
  const now = new Date();
  const list = Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const lastDay = new Date(year, month, 0).getDate();
    const start = `01/${String(month).padStart(2, '0')}`;
    const end = `${String(lastDay).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
    const label = `${d.toLocaleString('en-US', { month: 'long' })} ${year} (${start}–${end})`;
    return { value, label };
  });
  // Ensure the invoice's existing billing period is in the list (it may be >13 months old)
  if (currentValue && !list.find((m) => m.value === currentValue)) {
    const [y, mo] = currentValue.split('-');
    const year = Number.parseInt(y, 10);
    const month = Number.parseInt(mo, 10);
    const d = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0).getDate();
    const start = `01/${String(month).padStart(2, '0')}`;
    const end = `${String(lastDay).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
    const label = `${d.toLocaleString('en-US', { month: 'long' })} ${year} (${start}–${end})`;
    list.push({ value: currentValue, label });
  }
  return list;
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISODate(d); // local date; toISOString() would shift a day east of UTC
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function SectionHeader({ n, title, sub }: { n: string; title: string; sub?: string }) {
  return (
    <div className="px-5 py-4 border-b">
      <h3 className="text-sm font-semibold">
        {n} · {title}
      </h3>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      <div className="h-9 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-foreground">
        {value}
      </div>
    </div>
  );
}

type DownloadPdfParams = {
  invoiceNumber: string;
  clientName: string;
  projectName: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  issueDate: string;
  dueDate: string;
  lines: PreviewLine[];
  rates: Record<string, number>;
  showDates: boolean;
  vatPct: number;
  subtotal: number;
  vatAmount: number;
  total: number;
  notes: string;
  paymentTerms: string;
  lateFee: string;
  currency: Currency;
};

function useDownloadInvoicePdf() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download(params: DownloadPdfParams) {
    setDownloading(true);
    setError(null);
    try {
      const [{ pdf }, { InvoicePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/client/components/invoices/InvoicePDF'),
      ]);
      const blob = await pdf(
        <InvoicePDF
          invoiceNumber={params.invoiceNumber}
          clientName={params.clientName}
          projectName={params.projectName}
          billingPeriodStart={params.billingPeriodStart}
          billingPeriodEnd={params.billingPeriodEnd}
          issueDate={params.issueDate}
          dueDate={params.dueDate}
          lines={params.lines}
          rates={params.rates}
          showNames
          showDates={params.showDates}
          vatPct={params.vatPct}
          subtotal={params.subtotal}
          vatAmount={params.vatAmount}
          total={params.total}
          notes={params.notes}
          paymentTerms={params.paymentTerms}
          lateFee={params.lateFee}
          currency={params.currency}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${params.invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF generation failed');
    } finally {
      setDownloading(false);
    }
  }

  return { downloading, error, download };
}

// ─── Main page (loading + mode switch) ───────────────────────────────────────

function InvoiceDetailPage() {
  useAdminGuard();
  const { id } = Route.useParams();

  const { data: invoice, isLoading } = trpc.invoices.get.useQuery({ id });

  if (isLoading) {
    return (
      <div className="-mx-6 -mt-6 -mb-6 flex items-center justify-center h-[calc(100vh-56px)]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">Invoice not found.</div>
    );
  }

  return invoice.status === 'PAID' ? (
    <InvoiceReadView invoice={invoice} />
  ) : (
    <InvoiceEditView invoice={invoice} invoiceId={id} />
  );
}

// ─── Edit view (draft only) ───────────────────────────────────────────────────

function InvoiceEditView({ invoice, invoiceId }: { invoice: InvoiceDetail; invoiceId: string }) {
  const navigate = useNavigate();

  const months = useMemo(
    () => getLast13Months(invoice.billing_period_start.slice(0, 7)),
    [invoice.billing_period_start],
  );
  const [billingPeriod, setBillingPeriod] = useState(() =>
    invoice.billing_period_start.slice(0, 7),
  );
  const [selectedClientName, setSelectedClientName] = useState(invoice.client_name);
  const [projectId, setProjectId] = useState(invoice.project_id ?? '');
  const [issueDate, setIssueDate] = useState(invoice.issue_date);
  const [dueDate, setDueDate] = useState(invoice.due_date);
  const [notes, setNotes] = useState(invoice.notes ?? '');
  const [paymentTerms, setPaymentTerms] = useState(invoice.payment_terms ?? 'Net 30');
  const [lateFee, setLateFee] = useState(invoice.late_fee ?? '');
  const [vatPct, setVatPct] = useState(invoice.vat_pct);
  const [currency, setCurrency] = useState<Currency>(invoice.currency as Currency);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showNames, setShowNames] = useState(true);
  const [showDates, setShowDates] = useState(false);
  const [groupByPerson, setGroupByPerson] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [needsLineReset, setNeedsLineReset] = useState(false);

  const [lines, setLines] = useState<LineItem[]>(() =>
    invoice.lines.map((l) => ({
      id: l.id,
      timesheet_entry_id: l.timesheet_entry_id,
      person_id: '',
      person_name: l.person_name,
      date: l.date,
      description: l.description,
      hours: l.hours,
      sort_order: l.sort_order,
      included: true,
      is_shadow: false,
      is_manual: false,
    })),
  );

  const [rates, setRates] = useState<Record<string, number>>(() => {
    const r: Record<string, number> = {};
    for (const l of invoice.lines) {
      if (!(l.person_name in r)) r[l.person_name] = l.rate;
    }
    return r;
  });

  const [personIncluded, setPersonIncluded] = useState<Record<string, boolean>>(() => {
    const r: Record<string, boolean> = {};
    for (const l of invoice.lines) r[l.person_name] = true;
    return r;
  });

  const [bpYear, bpMonth] = billingPeriod.split('-');
  const billingYear = Number.parseInt(bpYear, 10);
  const billingMonth = Number.parseInt(bpMonth, 10);
  const billingStart = `${billingYear}-${String(billingMonth).padStart(2, '0')}-01`;
  const billingEnd = (() => {
    const lastDay = new Date(Date.UTC(billingYear, billingMonth, 0)).getUTCDate();
    return `${billingYear}-${String(billingMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  })();

  const { data: projects = [] } = trpc.projects.list.useQuery();
  const { data: employees = [] } = trpc.employees.list.useQuery();
  const { data: tsLines } = trpc.invoices.timesheetLines.useQuery(
    { project_id: projectId, year: billingYear, month: billingMonth },
    { enabled: !!projectId && !Number.isNaN(billingYear) && !Number.isNaN(billingMonth) },
  );

  const utils = trpc.useUtils();
  const updateMutation = trpc.invoices.update.useMutation({
    onSuccess: () => {
      utils.invoices.list.invalidate();
      utils.invoices.get.invalidate({ id: invoiceId });
      navigate({ to: '/invoices', search: { status: 'ALL' } });
    },
    onError: (err) => setError(err.message),
  });

  const deleteMutation = trpc.invoices.delete.useMutation({
    onSuccess: () => {
      utils.invoices.list.invalidate();
      navigate({ to: '/invoices', search: { status: 'ALL' } });
    },
    onError: (err) => setError(err.message),
  });

  const markAsPaidMutation = trpc.invoices.updateStatus.useMutation({
    onSuccess: () => {
      utils.invoices.get.invalidate({ id: invoiceId });
      utils.invoices.list.invalidate();
    },
    onError: (err) => setError(err.message),
  });

  const canMarkAsPaid = invoice.status === 'SENT' || invoice.status === 'OVERDUE';

  const { downloading, error: downloadError, download } = useDownloadInvoicePdf();

  function handleDownload() {
    const clientName = selectedProject?.client_name ?? selectedClientName ?? '';
    download({
      invoiceNumber: invoice.number,
      clientName,
      projectName: selectedProject?.name ?? '',
      billingPeriodStart: billingStart,
      billingPeriodEnd: billingEnd,
      issueDate,
      dueDate,
      lines: previewLines,
      rates,
      showDates,
      vatPct,
      subtotal,
      vatAmount,
      total,
      notes,
      paymentTerms,
      lateFee,
      currency,
    });
  }

  // When tsLines arrives after project/period change, replace lines + preserve existing rates
  useEffect(() => {
    if (!tsLines || !needsLineReset) return;
    setNeedsLineReset(false);
    setLines(
      tsLines.map((e) => ({
        id: e.id,
        timesheet_entry_id: e.is_shadow ? null : e.id,
        person_id: e.person_id,
        person_name: e.person_name,
        date: e.date,
        description: e.description,
        hours: e.hours,
        sort_order: e.sort_order,
        included: true,
        is_shadow: e.is_shadow,
        is_manual: false,
      })),
    );
    setRates((prev) => {
      const next = { ...prev };
      for (const e of tsLines) {
        if (!(e.person_name in next)) next[e.person_name] = e.default_rate;
      }
      return next;
    });
    const nextPersonIncluded: Record<string, boolean> = {};
    for (const e of tsLines) nextPersonIncluded[e.person_name] = true;
    setPersonIncluded(nextPersonIncluded);
  }, [tsLines]);

  // For a draft on its already-selected project/period, surface anyone who has
  // logged time since the draft was last saved — without touching existing
  // lines (edits, exclusions, manual additions all stay as-is). Shadow entries
  // take priority: the server already omits a shadowed user's real entries
  // from tsLines, but a real line saved on the invoice *before* their shadow
  // override existed would otherwise sit alongside the new shadow line and
  // double the hours — so any such stale real line is excluded here too.
  useEffect(() => {
    if (!tsLines || invoice.status !== 'DRAFT') return;
    const shadowedNames = new Set(tsLines.filter((e) => e.is_shadow).map((e) => e.person_name));
    setLines((ls) => {
      const existingRealIds = new Set(ls.map((l) => l.timesheet_entry_id).filter(Boolean));
      const existingShadowKeys = new Set(
        ls.filter((l) => l.is_shadow).map((l) => `${l.person_name}|${l.date}`),
      );
      const missing = tsLines.filter((e) =>
        e.is_shadow
          ? !existingShadowKeys.has(`${e.person_name}|${e.date}`)
          : !existingRealIds.has(e.id),
      );
      const superseded = (l: LineItem) =>
        !l.is_shadow && !l.is_manual && shadowedNames.has(l.person_name);
      const needsSuppression = ls.some((l) => superseded(l) && l.included);
      if (missing.length === 0 && !needsSuppression) return ls;
      const base = needsSuppression
        ? ls.map((l) => (superseded(l) ? { ...l, included: false } : l))
        : ls;
      return [
        ...base,
        ...missing.map((e, i) => ({
          id: e.id,
          timesheet_entry_id: e.is_shadow ? null : e.id,
          person_id: e.person_id,
          person_name: e.person_name,
          date: e.date,
          description: e.description,
          hours: e.hours,
          sort_order: base.length + i,
          included: true,
          is_shadow: e.is_shadow,
          is_manual: false,
        })),
      ];
    });
    setRates((prev) => {
      const next = { ...prev };
      for (const e of tsLines) {
        if (!(e.person_name in next)) next[e.person_name] = e.default_rate;
      }
      return next;
    });
  }, [tsLines, invoice.status]);

  const clientNames = useMemo(
    () =>
      Array.from(
        new Set(projects.map((p) => p.client_name).filter((c): c is string => Boolean(c))),
      ).sort(),
    [projects],
  );

  const filteredProjects = useMemo(
    () =>
      selectedClientName ? projects.filter((p) => p.client_name === selectedClientName) : projects,
    [projects, selectedClientName],
  );

  const selectedProject = projects.find((p) => p.id === projectId);

  const people = useMemo(() => {
    const names = Array.from(new Set(lines.map((l) => l.person_name))).filter(Boolean);
    return names.map((name) => {
      const personLines = lines.filter((l) => l.person_name === name);
      const includedPersonLines = personLines.filter((l) => l.included);
      const hours = includedPersonLines.reduce((s, l) => s + l.hours, 0);
      const amount = hours * (rates[name] ?? 0);
      return {
        name,
        hours,
        amount,
        lineCount: personLines.length,
        includedCount: includedPersonLines.length,
        isShadow: personLines.some((l) => l.is_shadow),
        included: personIncluded[name] ?? true,
      };
    });
  }, [lines, rates, personIncluded]);

  const visibleLines = useMemo(
    () => lines.filter((l) => personIncluded[l.person_name] ?? true),
    [lines, personIncluded],
  );

  const subtotal = useMemo(
    () =>
      visibleLines
        .filter((l) => l.included)
        .reduce((s, l) => s + l.hours * (rates[l.person_name] ?? 0), 0),
    [visibleLines, rates],
  );
  const vatAmount = (subtotal * vatPct) / 100;
  const total = subtotal + vatAmount;

  const includedLines = useMemo(() => visibleLines.filter((l) => l.included), [visibleLines]);
  const allIncluded = useMemo(
    () => visibleLines.length > 0 && visibleLines.every((l) => l.included),
    [visibleLines],
  );
  const someIncluded = useMemo(() => visibleLines.some((l) => l.included), [visibleLines]);

  const previewLines = useMemo<PreviewLine[]>(
    () =>
      includedLines.map((l) => ({
        id: l.id,
        person_name: l.person_name,
        date: l.date,
        description: l.description,
        hours: l.hours,
        rate: rates[l.person_name] ?? 0,
      })),
    [includedLines, rates],
  );

  const allPeopleIncluded = useMemo(
    () => people.length > 0 && people.every((p) => p.included),
    [people],
  );
  const somePeopleIncluded = useMemo(() => people.some((p) => p.included), [people]);

  function setLine(id: string, patch: Partial<LineItem>) {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  function toggleAll() {
    const next = !someIncluded;
    setLines((ls) =>
      ls.map((l) => ((personIncluded[l.person_name] ?? true) ? { ...l, included: next } : l)),
    );
  }

  function addManualLine() {
    setGroupByPerson(false);
    setLines((ls) => [
      ...ls,
      {
        id: crypto.randomUUID(),
        timesheet_entry_id: null,
        person_id: '',
        person_name: '',
        date: new Date().toISOString().slice(0, 10),
        description: '',
        hours: 0,
        sort_order: ls.length,
        included: true,
        is_shadow: false,
        is_manual: true,
      },
    ]);
  }

  function commitRename(oldName: string, newName: string) {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) {
      setEditingName(null);
      return;
    }
    setLines((ls) =>
      ls.map((l) => (l.person_name === oldName ? { ...l, person_name: trimmed } : l)),
    );
    setRates((r) => {
      const next = { ...r, [trimmed]: r[oldName] ?? 0 };
      delete next[oldName];
      return next;
    });
    setPersonIncluded((r) => {
      const next = { ...r, [trimmed]: r[oldName] ?? true };
      delete next[oldName];
      return next;
    });
    setEditingName(null);
  }

  function handleSave(status: 'DRAFT' | 'SENT' | 'OVERDUE') {
    if (!selectedProject && !selectedClientName) {
      setError('Select a client.');
      return;
    }
    if (includedLines.some((l) => !l.person_name.trim())) {
      setError('Pick a person for every manual line, or remove it.');
      return;
    }
    setError(null);
    const clientName = selectedProject?.client_name ?? selectedClientName ?? '';
    updateMutation.mutate({
      id: invoiceId,
      project_id: projectId || null,
      client_name: clientName,
      billing_period_start: billingStart,
      billing_period_end: billingEnd,
      issue_date: issueDate,
      due_date: dueDate,
      status,
      currency,
      notes: notes.trim() || undefined,
      payment_terms: paymentTerms || undefined,
      late_fee: lateFee || undefined,
      vat_pct: vatPct,
      subtotal,
      vat_amount: vatAmount,
      total,
      lines: includedLines.map((l, i) => ({
        timesheet_entry_id: l.timesheet_entry_id,
        person_name: l.person_name,
        date: l.date,
        description: l.description,
        hours: l.hours,
        rate: rates[l.person_name] ?? 0,
        amount: l.hours * (rates[l.person_name] ?? 0),
        sort_order: i,
      })),
    });
  }

  return (
    <div className="-mx-6 -mt-6 -mb-6 flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-3 bg-background shrink-0">
        <div>
          <p className="text-xs text-muted-foreground">Invoices</p>
          <h1 className="text-lg font-bold tracking-tight leading-tight flex items-center gap-2">
            {invoice.number}
            <InvoiceStatusBadge status={invoice.status as InvoiceStatus} />
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setDeleteOpen(true)}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="mr-1.5 h-4 w-4" />
            Delete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/invoices', search: { status: 'ALL' } })}
          >
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-4 w-4" />
            )}
            Download PDF
          </Button>
          {invoice.status === 'DRAFT' ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSave('DRAFT')}
                disabled={updateMutation.isPending}
              >
                Save draft
              </Button>
              <Button
                size="sm"
                onClick={() => handleSave('SENT')}
                disabled={updateMutation.isPending}
              >
                <Check className="mr-1.5 h-4 w-4" />
                Issue invoice
              </Button>
            </>
          ) : (
            <>
              {canMarkAsPaid && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => markAsPaidMutation.mutate({ id: invoiceId, status: 'PAID' })}
                  disabled={markAsPaidMutation.isPending}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Mark as paid
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => handleSave(invoice.status as 'SENT' | 'OVERDUE')}
                disabled={updateMutation.isPending}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 2-column body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: composer */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 min-w-0">
          {(error || downloadError) && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2">
              {error || downloadError}
            </p>
          )}

          {/* 1. Invoice details */}
          <Card className="overflow-hidden p-0">
            <SectionHeader n="1" title="Invoice details" sub="Who is this for and when." />
            <CardContent className="p-5 grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select
                  value={selectedClientName}
                  onValueChange={(v) => {
                    setSelectedClientName(v);
                    setProjectId('');
                    setLines([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientNames.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Project</Label>
                <Select
                  value={projectId}
                  onValueChange={(v) => {
                    setProjectId(v);
                    setLines([]);
                    setNeedsLineReset(true);
                    const proj = projects.find((p) => p.id === v);
                    if (proj?.client_name) setSelectedClientName(proj.client_name);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project…" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Billing period</Label>
                <Select
                  value={billingPeriod}
                  onValueChange={(v) => {
                    setBillingPeriod(v);
                    setLines([]);
                    setNeedsLineReset(true);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Invoice # — disabled, immutable */}
              <div className="space-y-1.5">
                <Label>Invoice #</Label>
                <Input value={invoice.number} disabled className="font-mono opacity-60" />
              </div>
              <div className="space-y-1.5">
                <Label>Issue date</Label>
                <DatePicker
                  value={issueDate}
                  onChange={(date) => {
                    setIssueDate(date);
                    setDueDate(addDays(date, 30));
                  }}
                  required
                  aria-label="Issue date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  Due date{' '}
                  <span className="font-normal text-muted-foreground text-xs">
                    (Net 30 by default)
                  </span>
                </Label>
                <DatePicker
                  value={dueDate}
                  onChange={setDueDate}
                  min={issueDate || undefined}
                  required
                  presets={[14, 30, 45, 60].map((n) => ({
                    label: `Net ${n}`,
                    value: addDays(issueDate, n),
                  }))}
                  aria-label="Due date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR — Euro (€)</SelectItem>
                    <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* 2. People & rates */}
          <Card className="overflow-hidden p-0">
            <div className="px-5 py-4 border-b flex items-center gap-3">
              <Checkbox
                checked={allPeopleIncluded}
                onCheckedChange={() => {
                  const next = !somePeopleIncluded;
                  setPersonIncluded((prev) => {
                    const r = { ...prev };
                    for (const p of people) r[p.name] = next;
                    return r;
                  });
                }}
                aria-label="Toggle all people"
                className={!allPeopleIncluded && somePeopleIncluded ? 'opacity-50' : ''}
              />
              <div>
                <h3 className="text-sm font-semibold">2 · People &amp; rates</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Hourly rates loaded from profile; edit to override for this invoice.
                </p>
              </div>
            </div>
            {people.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {projectId
                  ? 'No timesheet entries found for this period.'
                  : 'Select a project and billing period to load entries.'}
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-[32px_1fr_100px_100px_120px] gap-3 px-5 py-2.5 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div />
                  <div>Person</div>
                  <div className="text-right">Hours</div>
                  <div className="text-right">Rate / h</div>
                  <div className="text-right">Subtotal</div>
                </div>
                {people.map((p) => (
                  <div
                    key={p.name}
                    className={[
                      'group grid grid-cols-[32px_1fr_100px_100px_120px] gap-3 px-5 py-3.5 border-b last:border-0 items-center',
                      !p.included ? 'opacity-45' : '',
                    ].join(' ')}
                  >
                    <Checkbox
                      checked={p.included}
                      onCheckedChange={() =>
                        setPersonIncluded((prev) => ({
                          ...prev,
                          [p.name]: !(prev[p.name] ?? true),
                        }))
                      }
                      aria-label={`Include ${p.name}`}
                    />
                    <div className="flex items-center gap-3 min-w-0">
                      <InitialsAvatar name={p.name} size={32} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {editingName === p.name ? (
                            <input
                              // biome-ignore lint/a11y/noAutofocus: intentional focus for inline rename UX
                              autoFocus
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={() => commitRename(p.name, editingValue)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitRename(p.name, editingValue);
                                if (e.key === 'Escape') setEditingName(null);
                              }}
                              className="text-sm font-medium bg-transparent border-b border-border outline-none w-full"
                            />
                          ) : (
                            <>
                              <div className="text-sm font-medium">{p.name}</div>
                              {p.isShadow && (
                                <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                                  Shadow
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingName(p.name);
                                  setEditingValue(p.name);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                                aria-label={`Rename ${p.name}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.includedCount} of {p.lineCount} entries included
                        </div>
                      </div>
                    </div>
                    <div className="text-right tabular-nums text-sm font-medium">
                      {p.hours.toFixed(1)}h
                    </div>
                    <div className="flex justify-end">
                      <div className="relative w-24">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {currencySymbol(currency)}
                        </span>
                        <Input
                          className="pl-6 pr-1 text-right text-sm tabular-nums h-8"
                          value={String(rates[p.name] ?? 0)}
                          onChange={(e) =>
                            setRates((r) => ({
                              ...r,
                              [p.name]: Number.parseFloat(e.target.value) || 0,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="text-right tabular-nums text-sm font-semibold">
                      {fmtAmount(p.amount, currency)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 3. Line items */}
          <Card className="overflow-hidden p-0">
            <div className="px-5 py-4 border-b flex justify-between items-center gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  3 · Line items{' '}
                  <span className="text-muted-foreground font-normal">from timesheet</span>
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Toggle entries in or out, edit hours or descriptions inline.
                </p>
              </div>
              <div className="flex rounded-md border overflow-hidden shrink-0">
                {(['Detailed', 'Group by person'] as const).map((label, i) => {
                  const active = i === 0 ? !groupByPerson : groupByPerson;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setGroupByPerson(i === 1)}
                      className={[
                        'px-3 py-1 text-xs font-medium transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 px-5 py-2.5 border-b bg-muted/40">
              <Checkbox
                checked={allIncluded}
                onCheckedChange={toggleAll}
                aria-label="Toggle all"
                className={!allIncluded && someIncluded ? 'opacity-50' : ''}
              />
              <span className="text-xs text-muted-foreground">
                {includedLines.length} of {visibleLines.length} entries ·{' '}
                {includedLines.reduce((s, l) => s + l.hours, 0).toFixed(1)}h
              </span>
              <div className="flex-1" />
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showNames}
                  onChange={(e) => setShowNames(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                Show person on invoice
              </label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDates}
                  onChange={(e) => setShowDates(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                Show dates on invoice
              </label>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={addManualLine}
              >
                <Plus className="h-3 w-3" />
                Add manual line
              </Button>
            </div>

            {/* Lines */}
            {lines.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {projectId ? 'No entries for this period.' : 'Select a project and period above.'}
              </div>
            ) : !groupByPerson ? (
              <div>
                <div className="grid grid-cols-[32px_64px_140px_1fr_72px_80px_100px] gap-3 px-5 py-2 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div />
                  <div>Date</div>
                  <div>Person</div>
                  <div>Description</div>
                  <div className="text-right">Hours</div>
                  <div className="text-right">Rate</div>
                  <div className="text-right">Amount</div>
                </div>
                {visibleLines.map((l, i) => {
                  const rate = rates[l.person_name] ?? 0;
                  const amount = l.hours * rate;
                  return (
                    <div
                      key={l.id}
                      className={[
                        'grid grid-cols-[32px_64px_140px_1fr_72px_80px_100px] gap-3 px-5 py-3 items-center',
                        i < visibleLines.length - 1 ? 'border-b' : '',
                        !l.included ? 'opacity-45' : '',
                      ].join(' ')}
                    >
                      <Checkbox
                        checked={l.included}
                        onCheckedChange={() => setLine(l.id, { included: !l.included })}
                      />
                      {l.is_manual ? (
                        <DatePicker
                          variant="ghost"
                          size="sm"
                          hideIcon
                          placeholder="Date"
                          value={l.date}
                          onChange={(date) => setLine(l.id, { date })}
                          formatValue={(iso) => iso.slice(5).replace('-', '/')}
                          aria-label="Line date"
                        />
                      ) : (
                        <div className="tabular-nums text-xs text-muted-foreground">
                          {l.date.slice(5).replace('-', '/')}
                        </div>
                      )}
                      {l.is_manual && !l.person_name ? (
                        <Select
                          value={l.person_id}
                          onValueChange={(v) => {
                            const emp = employees.find((e) => e.id === v);
                            if (!emp?.full_name) return;
                            setLine(l.id, { person_id: emp.id, person_name: emp.full_name });
                            setRates((r) =>
                              emp.full_name && !(emp.full_name in r)
                                ? { ...r, [emp.full_name]: emp.rate ?? 0 }
                                : r,
                            );
                            setPersonIncluded((r) => ({ ...r, [emp.full_name as string]: true }));
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Select person…" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <InitialsAvatar name={l.person_name} size={22} />
                          <span className="text-xs font-medium truncate">
                            {l.person_name.split(' ')[0]}
                          </span>
                        </div>
                      )}
                      <input
                        value={l.description}
                        onChange={(e) => setLine(l.id, { description: e.target.value })}
                        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs outline-none w-full transition-colors"
                      />
                      <input
                        defaultValue={l.hours.toFixed(1)}
                        key={`${l.id}-hours`}
                        onBlur={(e) =>
                          setLine(l.id, { hours: Number.parseFloat(e.target.value) || 0 })
                        }
                        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs tabular-nums text-right outline-none w-full transition-colors"
                      />
                      <div className="text-right tabular-nums text-xs text-muted-foreground">
                        {currencySymbol(currency)}
                        {rate}
                      </div>
                      <div className="text-right tabular-nums text-xs font-semibold">
                        {l.is_manual && !l.person_name ? (
                          <button
                            type="button"
                            onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Remove line"
                          >
                            <X className="h-3.5 w-3.5 ml-auto" />
                          </button>
                        ) : (
                          fmtAmount(amount, currency)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                {people
                  .filter((p) => p.included)
                  .map((p, pi, arr) => {
                    const pLines = visibleLines.filter((l) => l.person_name === p.name);
                    return (
                      <div key={p.name} className={pi < arr.length - 1 ? 'border-b' : ''}>
                        <div className="flex items-center gap-3 px-5 py-3 bg-muted/40">
                          <InitialsAvatar name={p.name} size={28} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{p.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {p.hours.toFixed(1)}h @ {currencySymbol(currency)}
                              {rates[p.name] ?? 0}/h
                            </div>
                          </div>
                          <div className="tabular-nums text-sm font-semibold">
                            {fmtAmount(p.amount, currency)}
                          </div>
                        </div>
                        {pLines.map((l) => (
                          <div
                            key={l.id}
                            className={[
                              'grid grid-cols-[32px_64px_1fr_72px_100px] gap-3 px-5 py-2.5 items-center border-t',
                              !l.included ? 'opacity-45' : '',
                            ].join(' ')}
                          >
                            <Checkbox
                              checked={l.included}
                              onCheckedChange={() => setLine(l.id, { included: !l.included })}
                            />
                            <div className="tabular-nums text-xs text-muted-foreground">
                              {l.date.slice(5).replace('-', '/')}
                            </div>
                            <input
                              value={l.description}
                              onChange={(e) => setLine(l.id, { description: e.target.value })}
                              className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs outline-none w-full transition-colors"
                            />
                            <input
                              defaultValue={l.hours.toFixed(1)}
                              key={`${l.id}-hours`}
                              onBlur={(e) =>
                                setLine(l.id, { hours: Number.parseFloat(e.target.value) || 0 })
                              }
                              className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs tabular-nums text-right outline-none w-full transition-colors"
                            />
                            <div className="text-right tabular-nums text-xs font-semibold">
                              {fmtAmount(l.hours * (rates[l.person_name] ?? 0), currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
              </div>
            )}
          </Card>

          {/* 4. Notes & totals */}
          <div className="grid grid-cols-[1fr_300px] gap-5">
            <Card className="overflow-hidden p-0">
              <SectionHeader n="4" title="Notes &amp; payment terms" />
              <CardContent className="p-5 flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label>Note on invoice</Label>
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Thank you for your business. Bank transfer to…"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Payment terms</Label>
                    <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on receipt'].map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Late fee</Label>
                    <Input
                      value={lateFee}
                      onChange={(e) => setLateFee(e.target.value)}
                      placeholder="e.g. 3% / month"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Totals
              </p>
              <div className="flex flex-col gap-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums font-medium">{fmtAmount(subtotal, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">VAT</span>
                  <div className="flex items-center gap-2">
                    <div className="relative w-16">
                      <Input
                        className="pr-5 text-right text-sm tabular-nums h-7"
                        defaultValue={String(vatPct)}
                        key={`${vatPct}-vat`}
                        onBlur={(e) => setVatPct(Number.parseFloat(e.target.value) || 0)}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                    <span className="tabular-nums text-sm text-muted-foreground w-20 text-right">
                      {fmtAmount(vatAmount, currency)}
                    </span>
                  </div>
                </div>
                <div className="h-px bg-border my-1" />
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-semibold">Total due</span>
                  <span className="tabular-nums text-2xl font-bold">
                    {fmtAmount(total, currency)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          <div className="pb-10" />
        </div>

        {/* Right: live preview */}
        <div className="w-[460px] border-l bg-muted/30 overflow-y-auto p-6 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Live preview
            </p>
          </div>
          <InvoicePreview
            invoiceNumber={invoice.number}
            clientName={selectedProject?.client_name ?? selectedClientName}
            projectName={selectedProject?.name ?? ''}
            billingPeriodStart={billingStart}
            billingPeriodEnd={billingEnd}
            issueDate={issueDate}
            dueDate={dueDate}
            lines={previewLines}
            rates={rates}
            showNames={showNames}
            showDates={showDates}
            vatPct={vatPct}
            subtotal={subtotal}
            vatAmount={vatAmount}
            total={total}
            notes={notes}
            paymentTerms={paymentTerms}
            lateFee={lateFee}
            currency={currency}
          />
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {invoice.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              {invoice.status === 'DRAFT'
                ? 'This cannot be undone. The invoice number will not be reused.'
                : 'This sent invoice will be permanently deleted. The invoice number will not be reused.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate({ id: invoiceId })}
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Read-only view (paid; issue date stays editable) ───────────────────────────────────

function InvoiceReadView({ invoice }: { invoice: InvoiceDetail }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDates, setShowDates] = useState(false);
  const { downloading, error: downloadError, download } = useDownloadInvoicePdf();

  const ratesFromLines = useMemo(() => {
    const r: Record<string, number> = {};
    for (const l of invoice.lines) {
      if (!(l.person_name in r)) r[l.person_name] = l.rate;
    }
    return r;
  }, [invoice.lines]);

  const previewLines = useMemo<PreviewLine[]>(
    () =>
      invoice.lines.map((l) => ({
        id: l.id,
        person_name: l.person_name,
        date: l.date,
        description: l.description,
        hours: l.hours,
        rate: l.rate,
      })),
    [invoice.lines],
  );

  const markAsPaidMutation = trpc.invoices.updateStatus.useMutation({
    onSuccess: () => {
      utils.invoices.get.invalidate({ id: invoice.id });
      utils.invoices.list.invalidate();
    },
    onError: (err) => setActionError(err.message),
  });

  function handleDownload() {
    download({
      invoiceNumber: invoice.number,
      clientName: invoice.client_name,
      projectName: invoice.project_name ?? '',
      billingPeriodStart: invoice.billing_period_start,
      billingPeriodEnd: invoice.billing_period_end,
      issueDate: invoice.issue_date,
      dueDate: invoice.due_date,
      lines: previewLines,
      rates: ratesFromLines,
      showDates,
      vatPct: invoice.vat_pct,
      subtotal: invoice.subtotal,
      vatAmount: invoice.vat_amount,
      total: invoice.total,
      notes: invoice.notes ?? '',
      paymentTerms: invoice.payment_terms ?? '',
      lateFee: invoice.late_fee ?? '',
      currency: invoice.currency as Currency,
    });
  }

  const canMarkAsPaid = invoice.status === 'SENT' || invoice.status === 'OVERDUE';

  const updateIssueDateMutation = trpc.invoices.updateIssueDate.useMutation({
    onSuccess: () => {
      setActionError(null);
      utils.invoices.get.invalidate({ id: invoice.id });
      utils.invoices.list.invalidate();
    },
    onError: (err) => setActionError(err.message),
  });

  return (
    <div className="-mx-6 -mt-6 -mb-6 flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-3 bg-background shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate({ to: '/invoices', search: { status: 'ALL' } })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs text-muted-foreground">Invoices</p>
            <h1 className="text-lg font-bold tracking-tight leading-tight flex items-center gap-2">
              {invoice.number}
              <InvoiceStatusBadge status={invoice.status as InvoiceStatus} />
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canMarkAsPaid && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAsPaidMutation.mutate({ id: invoice.id, status: 'PAID' })}
              disabled={markAsPaidMutation.isPending}
            >
              <Check className="mr-1.5 h-4 w-4" />
              Mark as paid
            </Button>
          )}
          <Button size="sm" onClick={handleDownload} disabled={downloading}>
            {downloading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-4 w-4" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      {/* 2-column body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: read-only fields */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 min-w-0">
          {(actionError || downloadError) && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2">
              {actionError || downloadError}
            </p>
          )}
          {/* Section 1: Invoice details */}
          <Card className="overflow-hidden p-0">
            <SectionHeader n="1" title="Invoice details" />
            <CardContent className="p-5 grid grid-cols-3 gap-4">
              <ReadOnlyField label="Client" value={invoice.client_name} />
              <ReadOnlyField label="Project" value={invoice.project_name ?? '—'} />
              <ReadOnlyField
                label="Billing period"
                value={`${fmtDate(invoice.billing_period_start)}–${fmtDate(invoice.billing_period_end)}`}
              />
              <ReadOnlyField label="Invoice #" value={invoice.number} />
              <div className="space-y-1.5">
                <Label htmlFor="paid-issue-date" className="text-muted-foreground text-xs">
                  Issue date
                </Label>
                <DatePicker
                  id="paid-issue-date"
                  value={invoice.issue_date}
                  loading={updateIssueDateMutation.isPending}
                  onChange={(value) => {
                    if (value === invoice.issue_date) return;
                    updateIssueDateMutation.mutate({ id: invoice.id, issue_date: value });
                  }}
                />
              </div>
              <ReadOnlyField label="Due date" value={fmtDate(invoice.due_date)} />
            </CardContent>
          </Card>

          {/* Section 2: Line items */}
          <Card className="overflow-hidden p-0">
            <SectionHeader
              n="2"
              title="Line items"
              sub={`${invoice.lines.length} entr${invoice.lines.length === 1 ? 'y' : 'ies'}`}
            />
            {invoice.lines.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No line items.</div>
            ) : (
              <div>
                <div className="grid grid-cols-[1fr_80px_80px_100px] gap-3 px-5 py-2 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div>Description</div>
                  <div className="text-right">Hours</div>
                  <div className="text-right">Rate</div>
                  <div className="text-right">Amount</div>
                </div>
                {invoice.lines.map((l, i) => (
                  <div
                    key={l.id}
                    className={`grid grid-cols-[1fr_80px_80px_100px] gap-3 px-5 py-3 items-center ${
                      i < invoice.lines.length - 1 ? 'border-b' : ''
                    }`}
                  >
                    <div>
                      <div className="text-sm">{l.description}</div>
                      <div className="text-xs text-muted-foreground">{l.person_name}</div>
                    </div>
                    <div className="text-right tabular-nums text-sm">{l.hours.toFixed(1)}h</div>
                    <div className="text-right tabular-nums text-sm text-muted-foreground">
                      {currencySymbol(invoice.currency as Currency)}
                      {l.rate.toFixed(2)}
                    </div>
                    <div className="text-right tabular-nums text-sm font-semibold">
                      {fmtAmount(l.amount, invoice.currency as Currency)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Section 3: Notes & totals */}
          <div className="grid grid-cols-[1fr_300px] gap-5">
            <Card className="overflow-hidden p-0">
              <SectionHeader n="3" title="Notes &amp; payment terms" />
              <CardContent className="p-5 flex flex-col gap-3">
                {invoice.notes ? (
                  <p className="text-sm text-muted-foreground leading-relaxed">{invoice.notes}</p>
                ) : (
                  <p className="text-sm text-muted-foreground/50 italic">No notes.</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <ReadOnlyField label="Payment terms" value={invoice.payment_terms ?? '—'} />
                  <ReadOnlyField label="Late fee" value={invoice.late_fee ?? '—'} />
                </div>
              </CardContent>
            </Card>

            <Card className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Totals
              </p>
              <div className="flex flex-col gap-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums font-medium">
                    {fmtAmount(invoice.subtotal, invoice.currency as Currency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT ({invoice.vat_pct}%)</span>
                  <span className="tabular-nums">
                    {fmtAmount(invoice.vat_amount, invoice.currency as Currency)}
                  </span>
                </div>
                <div className="h-px bg-border my-1" />
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-semibold">Total due</span>
                  <span className="tabular-nums text-2xl font-bold">
                    {fmtAmount(invoice.total, invoice.currency as Currency)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          <div className="pb-10" />
        </div>

        {/* Right: preview */}
        <div className="w-[460px] border-l bg-muted/30 overflow-y-auto p-6 shrink-0">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDates}
                onChange={(e) => setShowDates(e.target.checked)}
                className="h-3.5 w-3.5 rounded"
              />
              Show dates on invoice
            </label>
          </div>
          <InvoicePreview
            invoiceNumber={invoice.number}
            clientName={invoice.client_name}
            projectName={invoice.project_name ?? ''}
            billingPeriodStart={invoice.billing_period_start}
            billingPeriodEnd={invoice.billing_period_end}
            issueDate={invoice.issue_date}
            dueDate={invoice.due_date}
            lines={previewLines}
            rates={ratesFromLines}
            showNames
            showDates={showDates}
            vatPct={invoice.vat_pct}
            subtotal={invoice.subtotal}
            vatAmount={invoice.vat_amount}
            total={invoice.total}
            notes={invoice.notes ?? ''}
            paymentTerms={invoice.payment_terms ?? ''}
            lateFee={invoice.late_fee ?? ''}
            currency={invoice.currency as Currency}
          />
        </div>
      </div>
    </div>
  );
}
